// v2 conversion-event helper. Emits to Google Analytics (GA4) via the global
// gtag; also calls the shared `track` (now a no-op after PostHog removal, kept
// for interface compatibility). GA4 is the single analytics sink.
//
// PII guard: payloads pass through an ALLOWLIST of safe metadata keys. Raw user
// content (resume/note/job text, email, phone, LinkedIn URL, file names) must
// never be sent — pass counts, categories, and booleans instead. Unknown keys
// are dropped, so a future call site can't leak PII by accident.
import { track } from "../lib/analytics";
import { trafficType } from "./internalTraffic";
import { recordEvent } from "./v2Debug";

export const V2Events = Object.freeze({
  // The app became usable. Fired once per page load, on the first render where
  // auth has resolved — i.e. the moment a visitor can actually do something.
  //
  // Why this exists: over 2026-07-25..08-03 the ads delivered 70 paid visitors
  // to /login and only 35 ever emitted v2_login_intent_shown. Half the paid
  // cohort was invisible after page_view, and nothing distinguished "left
  // immediately" from "waited and the app never came up". Measured against
  // production, a throttled mobile connection takes ~5.7s from navigation to a
  // usable sign-in button while page_view fires at ~1.2s, so that window is the
  // prime suspect — but it was a suspect, not a reading. This event makes it a
  // reading. { surface, ready_band }
  APP_READY: "v2_app_ready",

  // Auth entrance
  OAUTH_CLICK: "v2_oauth_click", // { provider }
  EMAIL_LINK_REQUESTED: "v2_email_link_requested", // { success }
  // Which sign-in promise a visitor was shown, derived from the ad's own
  // allowlisted `?next=` destination. { surface: fit | collect | default }
  LOGIN_INTENT_SHOWN: "v2_login_intent_shown",
  // A sign-in actually succeeded. Distinct from OAUTH_CLICK (intent) and from
  // the /auth/callback page_view, which also fires when the provider returns an
  // error — one paid session in the measured window landed on
  // `?error=user_cancelled_login` and was indistinguishable from a success.
  //
  // useAuth already called `track(SIGN_IN_COMPLETE)`, but `track` has been a
  // no-op since PostHog was removed, so authentication has had no analytics
  // signal at all. Fires once per browser session on a genuine SIGNED_IN
  // transition — never on a session restore, never on a token refresh.
  // { provider, is_new }
  AUTH_COMPLETED: "v2_auth_completed",

  // Setup / settings
  // The authenticated workspace shell mounted with a real user. Stage 5 of the
  // funnel: 9 of the 10 authenticated paid visitors in the measured window never
  // saved any evidence, and there was no event marking that they arrived at all
  // — SETUP_MODAL_ACCEPTED only fires for those who accept the setup modal, so
  // anyone who dismissed it left no trace. { surface }
  WORKSPACE_OPENED: "v2_workspace_opened",
  SETUP_MODAL_ACCEPTED: "v2_setup_modal_accepted",
  SEARCH_GOALS_SAVED: "v2_search_goals_saved", // { target_roles_count, must_haves_count, nice_to_haves_count }
  PROFILE_SAVED: "v2_profile_saved",

  // Collect (shoebox)
  EVIDENCE_UPLOAD_STARTED: "v2_evidence_upload_started", // { mode, file_count }
  EVIDENCE_UPLOAD_SUCCEEDED: "v2_evidence_upload_succeeded", // { mode, file_count }
  EVIDENCE_UPLOAD_FAILED: "v2_evidence_upload_failed", // { mode, file_count, error_category }
  NOTE_ADDED: "v2_note_added", // { success }

  // Chat
  CHAT_MESSAGE_SENT: "v2_chat_message_sent", // { captured_count }
  ACCOUNT_DELETED: "v2_account_deleted", // self-service account deletion (no PII)
  // Subscription cancellation. Carries only a coarse outcome enum — never a
  // Stripe id, customer, amount, period, or date. Billing facts stay out of
  // analytics entirely.
  SUBSCRIPTION_CANCELED: "v2_subscription_canceled", // { reason_code }

  // Find fit
  MATCH_SUBMITTED: "v2_match_submitted", // { job_count }
  MATCH_COMPLETED: "v2_match_completed", // { match_count }
  JOB_SCREENSHOT_UPLOADED: "v2_job_screenshot_uploaded", // { file_count, success }
  // Source-handling transparency (deterministic job source handling). Privacy-safe:
  // coarse reason codes only — never the URL, source name, or job text.
  JOB_SOURCE_GATED: "v2_job_source_gated", // { reason_code } — a gated/paste-required source was pasted
  PUBLIC_FETCH_FAILED: "v2_public_fetch_failed", // { reason_code } — an allowed fetch didn't yield a posting
  // Find public jobs (compliant public/official sources). Privacy-safe: counts + tier enum only.
  PUBLIC_JOBS_OPENED: "v2_public_jobs_opened",
  PUBLIC_JOBS_SEARCH_STARTED: "v2_public_jobs_search_started",
  PUBLIC_JOBS_RESULTS_RETURNED: "v2_public_jobs_results_returned", // { match_count }
  PUBLIC_JOBS_NO_RESULTS: "v2_public_jobs_no_results",
  PUBLIC_JOBS_SOURCE_FAILED: "v2_public_jobs_source_failed", // { source }
  PUBLIC_JOB_SELECTED: "v2_public_job_selected", // { fit_tier }
  PUBLIC_JOB_APPLY_OPENED: "v2_public_job_apply_opened",
  PUBLIC_JOB_USED_FOR_FIT: "v2_public_job_used_for_fit", // { fit_tier }
  PUBLIC_JOBS_RELEVANCE_BUCKET: "v2_public_jobs_relevance_bucket", // { fit_tier } — top-result tier
  // v2 Alignment scoring (PR-A2). Six events, one per observable moment of the
  // journey, so the funnel can be seen without inventing a parallel vocabulary.
  ALIGNMENT_SURFACE_VIEWED: "v2_alignment_surface_viewed",
  ALIGNMENT_SCORE_STARTED: "v2_alignment_score_started",
  ALIGNMENT_RESUME_REQUIRED: "v2_alignment_resume_required",
  ALIGNMENT_SCORE_SUCCEEDED: "v2_alignment_score_succeeded",
  ALIGNMENT_SCORE_FAILED: "v2_alignment_score_failed",
  ALIGNMENT_CONTINUE_CLICKED: "v2_alignment_continue_clicked",

  PACKAGE_BUILD_STARTED: "v2_package_build_started",
  PACKAGE_BUILD_SUCCEEDED: "v2_package_build_succeeded", // { has_cover_letter, via_workspace_adapter }
  PACKAGE_BUILD_FAILED: "v2_package_build_failed", // { error_category }
  PACKAGE_BUILD_BLOCKED: "v2_package_build_blocked", // { hard_missing_count } — preflight gap modal
  // The MVP adapter is the selected production path (owner decision,
  // 2026-08-13) — the frontend always targets it when the source posting is
  // unambiguous. This fires ONLY on the one signal that means "the backend's
  // own mvp_workspace_integration_enabled flag is off" (an exact 404 from
  // POST /v2/package/mvp) and the request transparently completed on the
  // legacy pipeline instead. It is the sole observable trace of that
  // emergency-rollback lever actually firing for a real customer request.
  PACKAGE_BUILD_ADAPTER_ROLLBACK: "v2_package_build_adapter_rollback",
  PACKAGE_QUOTA_BLOCKED: "v2_package_quota_blocked", // free-tier package limit hit (no payload)

  // Paid/SEO landing entry (funnel recovery). Fired when a visitor leaves a
  // landing article for the product. Coarse enums only — never the URL or query.
  LANDING_CTA_CLICKED: "v2_landing_cta_clicked", // { surface, placement, target }
  // The landing page's orientation block reached the screen. PROOF OF RENDER
  // ONLY — see useValuePropViewed. It is the denominator for
  // LANDING_CTA_CLICKED, not a claim that anyone understood anything.
  // { surface }
  VALUE_PROP_VIEWED: "v2_value_prop_viewed",
  // The visitor arrived at the workspace station a URL named — i.e. the ad's
  // promised destination survived sign-in. Distinct from WORKSPACE_OPENED, which
  // fires for every workspace arrival including a bare /workspace: this one only
  // fires when the URL asked for a specific station, so the two together say
  // whether continuation worked. { surface: one of WORKSPACE_STATIONS }
  WORKFLOW_RESUMED: "v2_workflow_resumed",

  // Pricing
  PLAN_SELECTED: "v2_plan_selected", // { plan }
  CHECKOUT_STARTED: "v2_checkout_started", // { plan }
  CHECKOUT_FAILED: "v2_checkout_failed", // { plan, error_category }
  CHECKOUT_COMPLETED: "v2_checkout_completed", // { plan, success } — fired once on Stripe success return

  // Upgrade intent + billing management (added 2026-07-08 — purchase-avenue
  // instrumentation gaps found by the GA4 purchase audit)
  UPGRADE_CTA_CLICKED: "v2_upgrade_cta_clicked", // { surface: account | quota_modal }
  BILLING_PORTAL_OPENED: "v2_billing_portal_opened", // { success }
});

// Safe metadata keys only — anything else is dropped.
const ALLOWED_KEYS = new Set([
  // How long the visitor waited for the app to become usable, as a coarse band
  // — never a raw millisecond figure, which on a low-traffic property is close
  // to a per-visitor fingerprint.
  "ready_band", // enum: fast | ok | slow | very_slow
  // Whether this browser has been marked as ours. See internalTraffic.js: it is
  // set only by an explicit ?fp_internal=1 visit, so it never guesses. Thirty-six
  // of the 111 sessions GA4 labelled "cpc" over 2026-07-25..08-03 came from one
  // city and carried no Google campaign id; nothing in the payload could tell
  // them apart from a customer.
  "traffic_type", // enum: internal | external
  "is_new", // boolean — first sign-in for this account, from Supabase created_at
  "provider",
  "mode",
  "plan",
  "success",
  "screen",
  "surface", // enum: account | quota_modal | pricing | ats_landing — where a CTA was clicked;
  //            also fit | collect | default — which sign-in promise was shown;
  //            also login | home | ats_landing | keyword_landing — which surface became ready;
  //            also one of WORKSPACE_STATIONS — which promised station was resumed.
  //            Always a member of a fixed, code-defined set. Never a URL, query,
  //            role title, employer, or anything a customer typed.
  "placement", // enum: top | bottom — where on a landing page the CTA sat
  "target", // enum: login | pricing — where a landing CTA points
  "file_count",
  "job_count",
  "match_count",
  "captured_count",
  "target_roles_count",
  // Alignment scoring: a coarse band and counts only. The score itself is a
  // number about a person, so it is bucketed rather than reported exactly, and
  // no resume, job, evidence or identifier value is ever eligible.
  "score_band",
  "evidence_item_count",
  "evidence_truncated",
  "job_source",
  "missing_count",
  "matched_count",
  "must_haves_count",
  "nice_to_haves_count",
  "hard_missing_count",
  "has_cover_letter",
  "error_category",
  "reason_code", // enum source-handling reason (e.g. automation_restricted, public_fetch_failed)
  "fit_tier", // enum: strong_match | possible_match | stretch_match | weak_match
  "source", // connector name enum (the_muse | greenhouse | usajobs) — never an employer/person name
]);

export function sanitizeProps(props = {}) {
  const out = {};
  for (const [k, v] of Object.entries(props)) {
    if (!ALLOWED_KEYS.has(k)) continue;
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") out[k] = v;
  }
  return out;
}

// Coarse error bucket — never forward raw error messages (they can embed user
// content, filenames, or emails).
export function errorCategory(err) {
  // Status first, message second. Matching only on message text meant a real
  // 429 whose message reads "Too many analyses in a short period" — containing
  // neither "429" nor "rate" — was categorised as a generic app error. The
  // message fallback stays for throws that carry no status.
  const status = Number(err?.status);
  if (Number.isFinite(status) && status > 0) {
    if (status === 401 || status === 403) return "auth";
    if (status === 429) return "rate_limit";
    if (status === 413) return "too_large";
    if (status >= 500) return "server";
  }
  const msg = String(err?.message || err || "").toLowerCase();
  if (!msg) return "unknown";
  if (/(network|fetch|timeout|abort)/.test(msg)) return "network";
  if (/(401|403|auth|token|sign)/.test(msg)) return "auth";
  if (/(429|rate)/.test(msg)) return "rate_limit";
  if (/(413|too large|size)/.test(msg)) return "too_large";
  if (/(5\d\d|server)/.test(msg)) return "server";
  return "app";
}

export function trackV2(event, props = {}) {
  // traffic_type rides on every v2 event, and is computed here rather than
  // passed by call sites: a report that must exclude our own devices can only do
  // that if the marker is on all of them, and one call site forgetting to pass
  // it would look exactly like a customer. It is set last so a caller cannot
  // spoof it through props.
  const safe = { experience: "v2", ...sanitizeProps(props),
                 traffic_type: trafficType() };
  try { recordEvent(event, sanitizeProps(props)); } catch { /* debug buffer must never break the app */ }
  try { track(event, safe); } catch { /* analytics must never break the app */ }
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", event, safe);
    }
  } catch { /* no-op */ }
}
